import type {
  GeoBrowseDisease,
  GeoBrowseEnrichmentParams,
  GeoBrowseEnrichmentRow,
  GeoBrowseGene,
  GeoBrowseGeneStudy,
  GeoBrowseGenesPage,
  GeoBrowseGenesParams,
  GeoBrowseMeta,
  GeoBrowsePcaPoint,
  GeoBrowseRecurrentGene,
  GeoBrowseSample,
  GeoBrowseStudy,
  GeoBrowseStudyDetail,
  GeoBrowseThresholds,
  GeoBrowseVolcanoPoint,
  GeoBrowseDegRow,
} from '../types';
import {
  MOCK_DISEASES,
  MOCK_GENES,
  MOCK_META,
  filterStudiesByCui,
  getMockComparisons,
  getMockEnrichment,
  getMockGeneStudies,
  getMockGeneStudySummary,
  getMockRecurrentGenes,
  getMockPca,
  getMockSamples,
  getMockStudyDetail,
  getMockTopDegs,
  getMockVolcano,
  sortGenes,
} from '../data/mockGeoBrowseData';
import {
  mapApiDegRow,
  mapApiDisease,
  mapApiEnrichmentRow,
  mapApiGene,
  mapApiPcaPoint,
  mapApiSample,
  mapApiStudy,
  mapApiStudyDetail,
  mapApiVolcanoPoint,
  unwrapApiList,
  type ApiDegRow,
  type ApiDisease,
  type ApiEnrichmentRow,
  type ApiGene,
  type ApiPcaPoint,
  type ApiSample,
  type ApiStudy,
  type ApiStudyDetail,
  type ApiVolcanoPoint,
} from './geoBrowseApiMappers';
import { useAuthStore } from '@/store/authStore';

/**
 * Base URL for ExpressNET / GEO browse API.
 * Leave empty to use local mocks. When the backend is live, set e.g.:
 *   REACT_APP_GEO_BROWSE_API_URL=https://web.test.medbioinformatics.com/api/geo-browse
 */
const API_BASE = process.env.REACT_APP_GEO_BROWSE_API_URL?.replace(/\/$/, '') ?? '';
const USE_MOCK = !API_BASE;
const MOCK_DELAY_MS = 300;

function comparisonQuery(comparisonId?: string): Record<string, string | undefined> {
  return comparisonId ? { comparison_id: comparisonId } : {};
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function mockFetch<T>(fn: () => T): Promise<T> {
  await delay(MOCK_DELAY_MS);
  return fn();
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().authorization;
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

async function fetchJson<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`ExpressNET API error: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export function isGeoBrowseMockMode(): boolean {
  return USE_MOCK;
}

export async function fetchGeoMeta(): Promise<GeoBrowseMeta> {
  if (USE_MOCK) {
    return mockFetch(() => MOCK_META);
  }
  return fetchJson<GeoBrowseMeta>('/meta');
}

export async function fetchDiseases(thresholds: GeoBrowseThresholds): Promise<GeoBrowseDisease[]> {
  if (USE_MOCK) {
    return mockFetch(() => MOCK_DISEASES);
  }
  const rows = await fetchJson<ApiDisease[]>(`/diseases${buildQuery(thresholds)}`);
  return rows.map(mapApiDisease);
}

export async function fetchStudies(thresholds: GeoBrowseThresholds, cui?: string): Promise<GeoBrowseStudy[]> {
  if (USE_MOCK) {
    return mockFetch(() => filterStudiesByCui(cui));
  }
  const rows = await fetchJson<ApiStudy[]>(`/studies${buildQuery({ ...thresholds, cui })}`);
  return rows.map(mapApiStudy);
}

export async function fetchGenes(params: GeoBrowseGenesParams): Promise<GeoBrowseGenesPage> {
  if (USE_MOCK) {
    return mockFetch(() => {
      const sorted = sortGenes(MOCK_GENES, params.sort ?? 'n_diseases_sig');
      const offset = params.offset ?? 0;
      const limit = params.limit ?? sorted.length;
      return {
        rows: sorted.slice(offset, offset + limit),
        total: sorted.length,
      };
    });
  }

  const offset = params.offset ?? 0;
  const limit = params.limit;
  const payload = await fetchJson<unknown>(`/genes${buildQuery(params)}`);
  const { rows, total } = unwrapApiList<ApiGene>(payload);
  const mapped = rows.map(mapApiGene);

  let resolvedTotal = total;
  if (resolvedTotal == null) {
    if (limit != null && mapped.length >= limit) {
      resolvedTotal = offset + mapped.length + 1;
    } else {
      resolvedTotal = offset + mapped.length;
    }
  }

  return { rows: mapped, total: resolvedTotal };
}

export async function fetchRecurrentGenes(): Promise<GeoBrowseRecurrentGene[]> {
  if (USE_MOCK) {
    return mockFetch(() => getMockRecurrentGenes());
  }
  return fetchJson<GeoBrowseRecurrentGene[]>('/genes/recurrent');
}

export async function fetchGeneStudySummary(geneSymbol: string): Promise<GeoBrowseGeneStudy[]> {
  if (USE_MOCK) {
    return mockFetch(() => getMockGeneStudySummary(geneSymbol));
  }
  return fetchJson<GeoBrowseGeneStudy[]>(`/genes/${encodeURIComponent(geneSymbol)}/study-summary`);
}

export async function fetchGeneStudies(
  geneSymbol: string,
  thresholds: GeoBrowseThresholds,
): Promise<GeoBrowseGeneStudy[]> {
  if (USE_MOCK) {
    return mockFetch(() => getMockGeneStudies(geneSymbol, thresholds));
  }
  return fetchJson<GeoBrowseGeneStudy[]>(
    `/genes/${encodeURIComponent(geneSymbol)}/studies${buildQuery(thresholds)}`,
  );
}

export async function fetchStudyDetail(
  cui: string,
  accession: string,
  comparisonId?: string,
): Promise<GeoBrowseStudyDetail | null> {
  if (USE_MOCK) {
    return mockFetch(() => getMockStudyDetail(cui, accession, comparisonId));
  }
  try {
    const row = await fetchJson<ApiStudyDetail>(
      `/studies/${encodeURIComponent(cui)}/${encodeURIComponent(accession)}${buildQuery(comparisonQuery(comparisonId))}`,
    );
    return mapApiStudyDetail(row);
  } catch {
    return null;
  }
}

export async function fetchStudySamples(cui: string, accession: string): Promise<GeoBrowseSample[]> {
  if (USE_MOCK) {
    return mockFetch(() => getMockSamples(cui, accession));
  }
  const rows = await fetchJson<ApiSample[]>(
    `/studies/${encodeURIComponent(cui)}/${encodeURIComponent(accession)}/samples`,
  );
  return rows.map(mapApiSample);
}

export async function fetchComparisons(cui: string, accession: string): Promise<GeoBrowseStudy[]> {
  if (USE_MOCK) {
    return mockFetch(() => getMockComparisons(cui, accession));
  }
  const rows = await fetchJson<ApiStudy[]>(
    `/studies/${encodeURIComponent(cui)}/${encodeURIComponent(accession)}/comparisons`,
  );
  return rows.map(mapApiStudy);
}

export async function fetchVolcano(
  cui: string,
  accession: string,
  thresholds: GeoBrowseThresholds,
  comparisonId?: string,
): Promise<GeoBrowseVolcanoPoint[]> {
  if (USE_MOCK) {
    return mockFetch(() => getMockVolcano(cui, accession, thresholds, comparisonId));
  }
  const rows = await fetchJson<ApiVolcanoPoint[]>(
    `/studies/${encodeURIComponent(cui)}/${encodeURIComponent(accession)}/volcano${buildQuery({
      ...thresholds,
      ...comparisonQuery(comparisonId),
    })}`,
  );
  return rows.map(row => mapApiVolcanoPoint(row, thresholds));
}

export async function fetchPca(cui: string, accession: string): Promise<GeoBrowsePcaPoint[]> {
  if (USE_MOCK) {
    return mockFetch(() => getMockPca(cui, accession));
  }
  const rows = await fetchJson<ApiPcaPoint[]>(
    `/studies/${encodeURIComponent(cui)}/${encodeURIComponent(accession)}/pca`,
  );
  return rows.map(mapApiPcaPoint);
}

export async function fetchTopDegs(
  cui: string,
  accession: string,
  thresholds: GeoBrowseThresholds,
  topN = 50,
  comparisonId?: string,
): Promise<GeoBrowseDegRow[]> {
  if (USE_MOCK) {
    return mockFetch(() => getMockTopDegs(cui, accession, thresholds, comparisonId));
  }
  const rows = await fetchJson<ApiDegRow[]>(
    `/studies/${encodeURIComponent(cui)}/${encodeURIComponent(accession)}/top-degs${buildQuery({
      ...thresholds,
      top_n: topN,
      ...comparisonQuery(comparisonId),
    })}`,
  );
  return rows.map(mapApiDegRow);
}

export async function fetchEnrichment(
  cui: string,
  accession: string,
  params: GeoBrowseEnrichmentParams,
  comparisonId?: string,
): Promise<GeoBrowseEnrichmentRow[]> {
  if (USE_MOCK) {
    return mockFetch(() =>
      getMockEnrichment(params.collection ?? 'CURATED', params.fdr ?? 0.25, params.top_n ?? 10),
    );
  }
  const rows = await fetchJson<ApiEnrichmentRow[]>(
    `/studies/${encodeURIComponent(cui)}/${encodeURIComponent(accession)}/enrichment${buildQuery({
      collection: params.collection,
      fdr: params.fdr,
      top_n: params.top_n,
      ...comparisonQuery(comparisonId),
    })}`,
  );
  return rows.map(mapApiEnrichmentRow);
}

export function findGeneBySymbol(symbol: string): GeoBrowseGene | undefined {
  return MOCK_GENES.find(g => g.gene_symbol === symbol);
}
